import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuthUser, useSignOut } from 'react-auth-kit';

import {
  Card,
  Stack,
  Button,
  Container,
  Typography,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';

// sections
import { useForm } from 'react-hook-form';
import SendIcon from '@mui/icons-material/Send';
import { sendOneSignalNotification } from '../utils/api';

export default function DashboardAppPage() {
  const { register, handleSubmit, reset, formState: { errors } } = useForm();
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false); // Add the loading state

  const resetMessages = () => {
    setSuccessMessage('');
    setErrorMessage('');
  };

  const auth = useAuthUser();
  const signOut = useSignOut();

  const { token } = auth();

  const onSubmit = async (data) => {
    resetMessages();
    setIsLoading(true); // Set loading to true when submitting the form

    const { messageTitle, messageUrl, messageBody } = data;
    const title = messageTitle;
    const url = messageUrl;
    const body = messageBody;

    try {
      await sendOneSignalNotification(title, url, body, token, signOut);
      setSuccessMessage('Tilkynning hefur verið send í OneSignal kerfið!');
      reset();
    } catch (error) {
      setErrorMessage(error.message || 'Villa kom upp! Athugið að titill og texti þurfa að vera útfyllt. URL er valkvætt.');
    } finally {
      setIsLoading(false); // Set loading to false when the operation is complete
    }
  };

  return (
    <>
      <Helmet>
        <title> Tilkynningar | Þúsund Fjalir ehf </title>
      </Helmet>

      <Container maxWidth="lg">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Senda Tilkynningu
          </Typography>
        </Stack>
        <Card>
          {successMessage && (
            <Alert severity="success">
              {successMessage}
            </Alert>
          )}
          {errorMessage && (
            <Alert severity="error">
              {errorMessage}
            </Alert>
          )}

          <Typography mx={4} my={4} variant="body1" color="gray">Athugið að tilkynningar berast eftir nokkrar mínútur.</Typography>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={2} sx={{ p: 3 }}>
              <Stack direction="row" spacing={2}>
                <TextField
                  {...register('messageTitle', { required: true })}
                  name="messageTitle"
                  label="Titill"
                  variant="outlined"
                  fullWidth
                  autoComplete="off"
                  error={!!errors.messageTitle}
                  helperText={errors.messageTitle && 'Vinsamlegast sláðu inn titil'}
                />
              </Stack>
              <Stack direction="column" spacing={0}>
                <Typography variant="body2" color="gray">Valkvætt (Slóð sem opnast ef notandi smellir á tilkynninguna)</Typography>
                <TextField
                  {...register('messageUrl', { required: false })}
                  name="messageUrl"
                  label="URL"
                  variant="outlined"
                  fullWidth
                  autoComplete="off"
                  error={!!errors.messageUrl}
                  helperText={errors.messageUrl && 'Vinsamlegast sláðu inn slóð ef þú vilt'}
                />
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField
                  {...register('messageBody', { required: true })}
                  name="messageBody"
                  label="Texti"
                  multiline
                  minRows={4}
                  variant="outlined"
                  fullWidth
                  autoComplete="off"
                  error={!!errors.messageBody}
                  helperText={errors.messageBody && 'Vinsamlegast sláðu inn texta'}
                />
              </Stack>
            </Stack>
            <br /><br />
            {isLoading ? ( // Render the circular progress bar if loading is true
              <Button
                variant="contained"
                disabled
                startIcon={<CircularProgress size={20} />} // Add the circular progress icon
                sx={{
                  position: 'absolute',
                  bottom: '10px',
                  right: '10px',
                  margin: '10px',
                }}
              >
                Stofna
              </Button>
            ) : (
              <Button
                variant="contained"
                type="submit"
                startIcon={<SendIcon />}
                sx={{
                  position: 'absolute',
                  bottom: '10px',
                  right: '10px',
                  margin: '10px',
                }}
              >
                Senda
              </Button>
            )}
          </form>
        </Card>
      </Container>
    </>
  );
}
